import React from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Link,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import { useSessionContext } from 'session'
import { type CommitteeMember } from 'pages'
import { type HttpResponse } from 'interfaces'
import { cardStyles, IconButtonWithTooltip, useUsPrefixLuts } from 'components'
import { DragAndDropStack } from 'components/common/drag-and-drop/drag-and-drop-stack'
import { formatFullName } from 'helpers'
import { US_COMMITTEE_MEMBER_ROLES } from 'utils'

const OFFICER_ROLE_IDS: number[] = [
  US_COMMITTEE_MEMBER_ROLES.FED_COMMITTEE_OFFICER,
  US_COMMITTEE_MEMBER_ROLES.FED_CAUCUS_OFFICER,
  US_COMMITTEE_MEMBER_ROLES.STATE_COMMITTEE_OFFICER,
  US_COMMITTEE_MEMBER_ROLES.STATE_CAUCUS_OFFICER
]

interface CommitteeMemberGroupCardProps {
  title: string
  members: CommitteeMember[]
  stackId: string
  loading: boolean
  onAdd: () => void
  onRetire: (member: CommitteeMember) => void
  updateDisplayOrder: (items: CommitteeMember[], itemSelected: CommitteeMember) => Promise<HttpResponse<CommitteeMember[]>>
}

export const CommitteeMemberGroupCard: React.FC<CommitteeMemberGroupCardProps> = ({
  title,
  members,
  stackId,
  loading,
  onAdd,
  onRetire,
  updateDisplayOrder
}) => {
  const { openNewTab } = useSessionContext()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()

  const renderRow = (comMember: CommitteeMember, index: number): React.ReactNode => {
    const { member } = comMember
    return (
      <ListItem
        component="div"
        divider={index < members.length - 1}
        secondaryAction={
          <IconButtonWithTooltip
            label="Retire Member"
            color="secondary"
            disabled={loading}
            onClick={() => { onRetire(comMember) }}
            icon={<HourglassEmptyIcon />}
          />
        }
      >
        <DragIndicatorIcon sx={{ color: 'text.disabled', mr: 1 }} />
        <Typography color="text.secondary" sx={{ minWidth: 32 }}>{index + 1}.</Typography>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Link
                component="button"
                variant="body1"
                underline="hover"
                onClick={() => { openNewTab(`/us/editMember/${member.id}/${member.person.id}`) }}
              >
                {formatFullName(member.person, usPrefixLutValues.prefixes)}
              </Link>
              {OFFICER_ROLE_IDS.includes(comMember.role.id) && (
                <Chip color="primary" label={comMember.legRole.code} />
              )}
            </Box>
          }
          secondary={`${member.person.party?.code ?? ''} · ${member.legseat.legdistrict.code}`}
        />
      </ListItem>
    )
  }

  return (
    <Card variant="outlined" sx={cardStyles}>
      <CardHeader
        title={<>{title} <Typography component="span" variant="h6" color="text.secondary">· {members.length}</Typography></>}
        titleTypographyProps={{ variant: 'h6' }}
        action={
          <Button variant="outlined" size="small" startIcon={<AddIcon />} disabled={loading} onClick={onAdd}>
            Add member
          </Button>
        }
      />
      <Divider />
      {members.length === 0
        ? (
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              No {title.toLowerCase()} members on this committee.
            </Typography>
          </CardContent>
          )
        : (
          <DragAndDropStack
            id={stackId}
            originalItems={members}
            itemRenderer={renderRow}
            updateDisplayOrder={updateDisplayOrder}
            disableDrag={loading || members.length <= 1}
            makeGray={false}
            spacing={0}
          />
          )}
    </Card>
  )
}
